// Pure math from ECONOMY §4. No state, no config types — just the formulas.
// Kept separate so the worked-example test can call them in isolation.

const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

/**
 * priceFactor = clamp( 2 − (price / sweetPrice), 0, 1.6 )
 * At sweetPrice → 1.0
 * At 2× sweetPrice → 0 (too dear)
 * At 0.5× sweetPrice → 1.5 (cheap, lots of units)
 */
export function priceFactor(price: number, sweetPrice: number): number {
  if (sweetPrice <= 0) return 0;
  return clamp(2 - price / sweetPrice, 0, 1.6);
}

/**
 * marketingBoost = min( marketingSpend / 30, 1.0 )
 * Diminishing returns, capped at +100%.
 */
export function marketingBoost(marketingSpend: number): number {
  return Math.min(Math.max(0, marketingSpend) / 30, 1.0);
}

/** Compute units sold per ECONOMY §4. Result is rounded then capped at capacity. */
export function unitsSold(args: {
  baseDemand: number;
  price: number;
  sweetPrice: number;
  marketingSpend: number;
  qualityFactor: number; // 0.8..1.2
  repFactor: number; // 0.8..1.2
  capacity: number;
}): number {
  const pf = priceFactor(args.price, args.sweetPrice);
  const mb = marketingBoost(args.marketingSpend);
  const raw = args.baseDemand * pf * (1 + mb) * args.qualityFactor * args.repFactor;
  const rounded = Math.round(raw);
  return Math.max(0, Math.min(args.capacity, rounded));
}

/** repFactor = 0.8 + 0.4 × (reputation/100). Bounded 0.8..1.2. */
export function repFactor(reputation: number): number {
  const clamped = clamp(reputation, 0, 100);
  return 0.8 + 0.4 * (clamped / 100);
}

/**
 * wellbeingFactor — how the owner's energy/balance affects output. A burnt-out or
 * neglected owner sells less; a healthy, balanced one sells a touch more.
 * Deliberately NEUTRAL (1.0) across the 50–69 band so the economy oracle (which
 * sits at the starting happiness of 50) is unaffected.
 *   ≥70 → 1.05   50–69 → 1.00   30–49 → 0.90   15–29 → 0.80   <15 → 0.70
 */
export function wellbeingFactor(happiness: number): number {
  const h = clamp(happiness, 0, 100);
  if (h >= 70) return 1.05;
  if (h >= 50) return 1.0;
  if (h >= 30) return 0.9;
  if (h >= 15) return 0.8;
  return 0.7;
}

export type BusinessWeeklyResult = {
  unitsSold: number;
  revenue: number;
  cogs: number;
  grossProfit: number;
  fixed: number;
  marketing: number;
  wages: number;
  activeIncome: number; // grossProfit − fixed − marketing − wages
};

/** Compute the weekly P&L numbers for one business instance. */
export function businessWeeklyResult(args: {
  baseDemand: number;
  price: number;
  sweetPrice: number;
  marketingSpend: number;
  qualityFactor: number;
  repFactor: number;
  capacity: number;
  cogsPerUnit: number;
  fixedPerWeek: number;
  wagesPerWeek?: number;
}): BusinessWeeklyResult {
  const units = unitsSold(args);
  const revenue = units * args.price;
  const cogs = units * args.cogsPerUnit;
  const grossProfit = revenue - cogs;
  const wages = args.wagesPerWeek ?? 0;
  const activeIncome = grossProfit - args.fixedPerWeek - args.marketingSpend - wages;
  return {
    unitsSold: units,
    revenue,
    cogs,
    grossProfit,
    fixed: args.fixedPerWeek,
    marketing: args.marketingSpend,
    wages,
    activeIncome,
  };
}
